/**
 * Currency → locale mapping for the backoffice i18n foundation.
 *
 * The operator's selected currency (from `OperatorCurrency.GetDefaultCurrency`)
 * drives BOTH money formatting (Intl.NumberFormat) and — in the upcoming task —
 * the active UI language. `resolveLocale` is the single place that turns a
 * currency code into a BCP-47 locale so the two never diverge.
 *
 * Kept BO-local (not in @softstudio/constants) since that package is not wired
 * into the backoffice today and "share with FE" is an open question, not a
 * requirement.
 */

/** Currency code (upper-cased) → BCP-47 locale used for number/currency formatting. */
export const CURRENCY_LOCALE_MAP: Record<string, string> = {
  INR: "en-IN",
  USD: "en-US",
  EUR: "de-DE",
  NPR: "ne-NP",
  PKR: "en-PK",
  LKR: "en-LK",
};

/** Fallback locale — matches the historical hardcoded `en-IN` so INR output is unchanged. */
export const DEFAULT_LOCALE = "en-IN";

/**
 * Locale used when NO currency is configured: neutral Western grouping
 * (`1,234,567`) with no currency symbol. Distinct from DEFAULT_LOCALE (en-IN,
 * Indian grouping) so a missing currency never implies an Indian-formatted INR.
 */
export const NO_CURRENCY_LOCALE = "en-US";

/** Locales the i18n layer is allowed to switch to. Keep in sync with CURRENCY_LOCALE_MAP values. */
export const SUPPORTED_LOCALES = Array.from(new Set([DEFAULT_LOCALE, ...Object.values(CURRENCY_LOCALE_MAP)]));

/**
 * Resolve a currency code to its formatting/UI locale.
 * Unknown or missing currency → DEFAULT_LOCALE (en-IN), so a value never renders
 * with a wrong-but-confident locale; at worst an unmapped code degrades to en-IN grouping.
 */
export const resolveLocale = (currency?: string | null): string => {
  if (!currency) return DEFAULT_LOCALE;

  return CURRENCY_LOCALE_MAP[currency.toUpperCase()] ?? DEFAULT_LOCALE;
};

/**
 * Resolve a currency code to its locale-correct symbol (e.g. INR → "₹", USD → "$").
 * Derived from Intl by formatting 0 as currency and stripping digits/separators,
 * so it stays in sync with whatever Intl renders for the operator's locale.
 * Useful when a component needs a bare symbol string (e.g. a MotionNumber prefix).
 *
 * No-currency behaviour matches `useMoneyFormatters`: a missing/null currency
 * returns "" (no symbol) rather than silently defaulting to INR — the same
 * silent-fallback bug this i18n work fixes elsewhere.
 */
export const getCurrencySymbol = (currency?: string | null, locale?: string | null): string => {
  if (!currency) return "";
  const loc = locale ?? resolveLocale(currency);
  try {
    const parts = new Intl.NumberFormat(loc, { style: "currency", currency, maximumFractionDigits: 0 }).formatToParts(0);

    return parts.find((p) => p.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
};
